'use client'

import { useLayoutLayers } from 'hooks/useLayoutLayers/useLayoutLayers'
import {
	createContext,
	type Dispatch,
	type SetStateAction,
	useEffect,
	useMemo,
	useState,
} from 'react'
import {
	getFromLocalStorage,
	setToLocalStorage,
} from 'utils/webStorage/localStorage'
import {
	IS_CHAT_MINIMIZED_KEY,
	IS_CHAT_OPEN_KEY,
	TAB_ID_LS_ID,
} from '../constants/constants'

interface ChatDialogContext {
	isDialogOpen: boolean
	setIsDialogOpen: Dispatch<SetStateAction<boolean>>
	isDialogMinimized: boolean
	setIsDialogMinimized: Dispatch<SetStateAction<boolean>>
	tabId: string | undefined
}

export const ChatbotDialogContext = createContext<
	ChatDialogContext | undefined
>(undefined)

interface ChatbotDialogProviderProps {
	readonly children: React.ReactNode
}

export function ChatbotDialogProvider({
	children,
}: ChatbotDialogProviderProps): React.ReactNode {
	const { application } = useLayoutLayers()

	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [isDialogMinimized, setIsDialogMinimized] = useState(false)

	const [tabId, setTabId] = useState<string | undefined>(undefined)

	useEffect(() => {
		if (isDialogOpen) {
			const newTabId = crypto.randomUUID()
			setToLocalStorage(TAB_ID_LS_ID, newTabId)
			setToLocalStorage(IS_CHAT_OPEN_KEY, true)
			setTabId(JSON.stringify(newTabId))
		}
		application?.removeAttribute('inert')
	}, [isDialogOpen])

	useEffect(() => {
		const hadChatDialogOpen = getFromLocalStorage(IS_CHAT_OPEN_KEY) === true
		const hadChatDialogMinimized =
			getFromLocalStorage(IS_CHAT_MINIMIZED_KEY) === true

		if (hadChatDialogOpen) {
			setIsDialogOpen(true)
		}

		if (hadChatDialogMinimized) {
			setIsDialogMinimized(true)
		}
	}, [])

	const value = useMemo(
		() => ({
			isDialogOpen,
			setIsDialogOpen,
			tabId,
			isDialogMinimized,
			setIsDialogMinimized,
		}),
		[
			isDialogOpen,
			setIsDialogOpen,
			tabId,
			isDialogMinimized,
			setIsDialogMinimized,
		]
	)

	return (
		<ChatbotDialogContext.Provider value={value}>
			{children}
		</ChatbotDialogContext.Provider>
	)
}
